import { useState, useEffect } from 'react';
import { Button, Card, Col, Row, TablePaginationConfig } from 'antd';
import { PatientsTable } from '../../components/dashboard/PatientsTable';
import {
  PatientSearchForm,
  PatientSearchFormValues,
} from '../../components/dashboard/PatientSearchForm';
import {
  useOrganizationFindAllSuspense,
  usePatientFindMany,
} from '../../../libs/ohat-api';
import { DownloadOutlined } from '@ant-design/icons';
import { format, getNowDownloadLabel } from '../../../utils/date';
import { patientFindMany } from '../../../libs/ohat-api';
import { Gender } from '../../../constants/gender';
import { PatientCareLevel } from '../../../constants/careLevel';
import { IndependentLifeLevel } from '../../../constants/independentLifeLevel';
import { DentalIntervention } from '../../../constants/dentalIntervention';
import { OrganizationType } from '../../../constants/orgType';
import { buildCsv, downloadCsv } from '../../../utils/csv';

const csvColumns = [
  { displayName: '組織名', id: 'organizationName' },
  { displayName: '都道府県', id: 'organizationPrefecture' },
  { displayName: '施設種別', id: 'organizationType' },
  { displayName: '患者名', id: 'name' },
  { displayName: '年齢', id: 'age' },
  { displayName: '性別', id: 'gender' },
  { displayName: '要介護度', id: 'careLevel' },
  { displayName: '自立度', id: 'independentLifeLevel' },
  { displayName: '歯科介入', id: 'dentalIntervention' },
  { displayName: '初回スコア', id: 'firstScore' },
  { displayName: '初回評価日', id: 'firstScoreDate' },
  { displayName: '2回目スコア', id: 'secondScore' },
  { displayName: '2回目評価日', id: 'secondScoreDate' },
  { displayName: '3回目スコア', id: 'thirdScore' },
  { displayName: '3回目評価日', id: 'thirdScoreDate' },
  { displayName: '最新スコア', id: 'latestScore' },
  { displayName: '最新評価日', id: 'latestScoreDate' },
  { displayName: '登録日', id: 'registeredAt' },
];

export function PatientListPage() {
  const { data: organizations } = useOrganizationFindAllSuspense();
  const [csvDownloading, setCsvDownloading] = useState(false);

  // 検索条件とページング状態
  const [searchParams, setSearchParams] = useState<any>({
    page: 1,
    limit: 20,
  });

  // APIからデータ取得
  const {
    data: apiResponse,
    isLoading: loading,
    refetch,
  } = usePatientFindMany(searchParams);

  const patients = apiResponse?.patients || [];
  const total = apiResponse?.total || 0;

  // 検索パラメータが変更されたらAPIを再取得
  useEffect(() => {
    refetch();
  }, [searchParams, refetch]);

  // CSVダウンロード処理
  const handleCsvDownload = async () => {
    try {
      setCsvDownloading(true);

      const CSV_PAGE_SIZE = 500; // 1回のリクエストで取得する件数
      const MAX_CONCURRENT = 3; // 同時リクエスト数の上限

      // 1ページ目を取得して総件数を確認
      const firstResponse = await patientFindMany({
        ...searchParams,
        page: 1,
        limit: CSV_PAGE_SIZE,
      });

      const csvTotal = firstResponse?.total || 0;
      const totalPages = Math.ceil(csvTotal / CSV_PAGE_SIZE);
      let allPatients = [...(firstResponse?.patients || [])];

      // 2ページ目以降を並列で取得（同時リクエスト数を制限）
      for (let i = 2; i <= totalPages; i += MAX_CONCURRENT) {
        const pagePromises = [];
        for (let j = i; j < i + MAX_CONCURRENT && j <= totalPages; j++) {
          pagePromises.push(
            patientFindMany({
              ...searchParams,
              page: j,
              limit: CSV_PAGE_SIZE,
            }),
          );
        }
        const responses = await Promise.all(pagePromises);
        for (const res of responses) {
          allPatients = allPatients.concat(res?.patients || []);
        }
      }

      // CSVデータ生成
      const csvRows = allPatients.map((patient: any) => ({
        name: patient.name || '',
        age: patient.age?.toString() || '',
        gender: Gender.getLabelByKey(patient.gender),
        careLevel: PatientCareLevel.getLabelByKey(patient.careLevel),
        independentLifeLevel: IndependentLifeLevel.getShortLabelByKey(
          patient.independentLifeLevel,
        ),
        dentalIntervention: DentalIntervention.getShortLabelByKey(
          patient.dentalIntervention,
        ),
        firstScore: patient.firstScore?.toString() || '',
        firstScoreDate: patient.firstScoreDate
          ? format(new Date(patient.firstScoreDate), 'YYYY/MM/DD HH:mm')
          : '',
        secondScore: patient.secondScore?.toString() || '',
        secondScoreDate: patient.secondScoreDate
          ? format(new Date(patient.secondScoreDate), 'YYYY/MM/DD HH:mm')
          : '',
        thirdScore: patient.thirdScore?.toString() || '',
        thirdScoreDate: patient.thirdScoreDate
          ? format(new Date(patient.thirdScoreDate), 'YYYY/MM/DD HH:mm')
          : '',
        latestScore: patient.latestScore?.toString() || '',
        latestScoreDate: patient.latestScoreDate
          ? format(new Date(patient.latestScoreDate), 'YYYY/MM/DD HH:mm')
          : '',
        organizationName: patient.organization?.name || '',
        organizationPrefecture: patient.organization?.prefecture || '',
        organizationType: OrganizationType.getLabelByKey(
          patient.organization?.type,
        ),
        registeredAt: patient.registeredAt
          ? format(new Date(patient.registeredAt), 'YYYY/MM/DD HH:mm')
          : '',
      }));

      // CSV文字列生成 → BOM付きでダウンロード（共通ユーティリティに集約）
      const csvContent = buildCsv(csvColumns, csvRows);
      downloadCsv(csvContent, `patients_${getNowDownloadLabel()}.csv`);
    } catch (error) {
      console.error('CSV download error:', error);
    } finally {
      setCsvDownloading(false);
    }
  };

  const pagination: TablePaginationConfig = {
    current: apiResponse?.page || 1,
    pageSize: apiResponse?.limit || 20,
    total: total,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    showTotal: (total) => `全 ${total} 件`,
  };

  const handleSearch = (values: PatientSearchFormValues) => {
    setSearchParams({
      ...values,
      page: 1,
      limit: searchParams.limit,
    });
  };

  const handleReset = () => {
    setSearchParams({
      page: 1,
      limit: 20,
    });
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setSearchParams({
      ...searchParams,
      page: newPagination.current || 1,
      limit: newPagination.pageSize || 20,
    });
  };

  return (
    <Row gutter={[16, 16]} style={{ flex: 1, maxWidth: 1400 }}>
      <Col md={24} xs={24}>
        <PatientSearchForm
          organizations={organizations || []}
          onSearch={handleSearch}
          onReset={handleReset}
        />
      </Col>

      <Col md={24} xs={24}>
        <Card
          title={`患者一覧 (全${total}件)`}
          extra={
            <Button
              icon={<DownloadOutlined />}
              onClick={handleCsvDownload}
              loading={csvDownloading}
            >
              CSVダウンロード
            </Button>
          }
        >
          <PatientsTable
            patients={patients}
            loading={loading}
            pagination={pagination}
            onTableChange={handleTableChange}
          />
        </Card>
      </Col>
    </Row>
  );
}
